/**
 * useSLAMonitor — Monitors message response time SLA compliance
 *
 * For each assigned client, checks if there are unresponded client messages
 * and compares elapsed time against SLA thresholds from am_sla_config.
 */

import { useEffect, useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";

export type SLAStatus = "ok" | "warning" | "breach";

export interface ClientSLAInfo {
  agentId: string;
  agentName: string;
  status: SLAStatus;
  /** Hours since last unresponded client message */
  elapsedHours: number;
  /** The SLA threshold being measured against */
  thresholdHours: number;
  /** Whether this is first response or follow-up */
  type: "first_response" | "follow_up";
  /** The unresponded message ID */
  messageId: string | null;
}

interface SLAConfig {
  firstResponseHours: number;
  followUpHours: number;
}

const DEFAULT_SLA: SLAConfig = {
  firstResponseHours: 4,
  followUpHours: 24,
};

export function useSLAMonitor(amId: string | null, clientAgentIds: string[]) {
  const [slaStatuses, setSlaStatuses] = useState<ClientSLAInfo[]>([]);
  const [slaConfig, setSlaConfig] = useState<SLAConfig>(DEFAULT_SLA);
  const [loading, setLoading] = useState(true);

  const evaluate = useCallback(async () => {
    if (!amId || clientAgentIds.length === 0) {
      setSlaStatuses([]);
      setLoading(false);
      return;
    }

    try {
      // 1. Fetch SLA config
      const { data: configData } = await supabase
        .from("am_sla_config")
        .select("first_response_hours, follow_up_hours")
        .eq("is_active", true)
        .limit(1)
        .maybeSingle();

      const config: SLAConfig = configData
        ? {
            firstResponseHours: configData.first_response_hours ?? DEFAULT_SLA.firstResponseHours,
            followUpHours: configData.follow_up_hours ?? DEFAULT_SLA.followUpHours,
          }
        : DEFAULT_SLA;
      setSlaConfig(config);

      // 2. Batch-fetch recent messages for all clients in a single query
      const { data: allMessages } = await supabase
        .from("am_messages")
        .select("id, agent_id, sender_role, created_at")
        .eq("am_id", amId)
        .in("agent_id", clientAgentIds)
        .order("created_at", { ascending: false });

      // Group by agent_id and keep only the last 2 messages per agent
      const messagesByAgent = new Map<string, typeof allMessages>();
      for (const msg of allMessages || []) {
        const existing = messagesByAgent.get(msg.agent_id);
        if (!existing) {
          messagesByAgent.set(msg.agent_id, [msg]);
        } else if (existing.length < 2) {
          existing.push(msg);
        }
      }

      const results: ClientSLAInfo[] = [];
      const now = Date.now();

      for (const agentId of clientAgentIds) {
        const messages = messagesByAgent.get(agentId);
        if (!messages || messages.length === 0) continue;

        const lastMessage = messages[0];

        // If the last message is from the client, AM hasn't responded
        if (lastMessage.sender_role === "client") {
          const messageTime = new Date(lastMessage.created_at).getTime();
          const elapsedHours = (now - messageTime) / (1000 * 60 * 60);

          const hasAMResponse = messages.length > 1 && messages[1].sender_role === "am";
          const type = hasAMResponse ? "follow_up" as const : "first_response" as const;
          const threshold = type === "first_response" ? config.firstResponseHours : config.followUpHours;

          let status: SLAStatus = "ok";
          if (elapsedHours >= threshold) {
            status = "breach";
          } else if (elapsedHours >= threshold * 0.75) {
            status = "warning";
          }

          if (status !== "ok") {
            results.push({
              agentId,
              agentName: "",
              status,
              elapsedHours: Math.round(elapsedHours * 10) / 10,
              thresholdHours: threshold,
              type,
              messageId: lastMessage.id,
            });
          }
        }
      }

      setSlaStatuses(results);
    } catch (err) {
      console.error("[useSLAMonitor] Error:", err);
    } finally {
      setLoading(false);
    }
  }, [amId, clientAgentIds.join(",")]);

  useEffect(() => {
    evaluate();
  }, [evaluate]);

  const breachCount = slaStatuses.filter((s) => s.status === "breach").length;
  const warningCount = slaStatuses.filter((s) => s.status === "warning").length;

  return { slaStatuses, slaConfig, breachCount, warningCount, loading, refetch: evaluate };
}
